import { useEffect, useRef } from 'react';

declare const __APP_BUILD__: string;

const CHECK_MS = 2 * 60 * 1000;

/**
 * Atualização automática da página, sem botão nem aviso de "nova versão".
 *
 * O build grava o mesmo id em `__APP_BUILD__` (dentro do bundle) e em `/version.json`.
 * Ao abrir, ao voltar o foco e a cada ~2 min a página busca o version.json (sem cache);
 * se o id mudou, recarrega sozinha. Enquanto `busy` for true (tela de confirmação,
 * diálogo de pagamento, código sendo digitado) o reload espera e acontece assim que
 * a ação termina.
 *
 * Não há service worker: o index.html é servido com `Cache-Control: no-cache` e os
 * assets têm hash no nome, então um reload sempre traz a versão nova.
 */
export function useAutoUpdate(busy: boolean) {
  const pending = useRef(false);
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    const atual = typeof __APP_BUILD__ !== 'undefined' ? __APP_BUILD__ : '';
    if (!atual) return undefined; // dev server
    let ativo = true;

    const check = async () => {
      if (pending.current) return;
      try {
        const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!r.ok) return;
        const { build } = await r.json();
        if (ativo && build && build !== atual) {
          pending.current = true;
          if (!busyRef.current) window.location.reload();
        }
      } catch { /* sem rede: tenta na próxima */ }
    };

    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    check();
    const timer = window.setInterval(check, CHECK_MS);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', check);
    window.addEventListener('online', check);
    // Chunk de um build antigo que já não existe no servidor: recarrega para pegar o novo.
    const onPreloadError = () => { pending.current = true; if (!busyRef.current) window.location.reload(); };
    window.addEventListener('vite:preloadError', onPreloadError);

    return () => {
      ativo = false;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', check);
      window.removeEventListener('online', check);
      window.removeEventListener('vite:preloadError', onPreloadError);
    };
  }, []);

  // Havia versão nova esperando a ação terminar: recarrega logo que fica livre.
  useEffect(() => {
    if (!busy && pending.current) window.location.reload();
  }, [busy]);
}
